import { useCallback, useEffect, useMemo, useState } from "react";
import { lumiFetch, fmtDateTime } from "./lumiApi";
import { toast } from "./lumiToast";

// Lumi admin: browse credit-usage ledger for any organisation.
// - Master list on top (search hotels/schools/charities)
// - Click a row → expand with filter chips + per-message ledger records
// - Reuses GET /api/lumi/hotels and GET /api/lumi/hotels/{id}/credit-usage

const KIND_LABEL = {
  chat_debit: "Chat message",
  chat_refund: "Chat refund",
  membership_start: "Membership starts",
  membership_renewal: "Membership renewal",
  addon_topup: "Add-on top-up",
  refund: "Refund",
  adjustment: "Manual adjustment",
};

export default function CreditUsageTab() {
  const [hotels, setHotels] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const qs = new URLSearchParams();
      if (q) qs.set("q", q);
      const r = await lumiFetch(`/hotels${qs.toString() ? `?${qs.toString()}` : ""}`);
      setHotels(r.hotels || []);
    } catch (e) {
      setError(e?.data?.detail || "Could not load organisations");
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => { load(); }, [load]);

  const totals = useMemo(() => {
    const t = { orgs: hotels.length, credits_in_pool: 0 };
    for (const h of hotels) {
      t.credits_in_pool += (h.monthly_credits_remaining || 0) + (h.addon_credits_remaining || 0);
    }
    return t;
  }, [hotels]);

  return (
    <div className="space-y-4 p-5" data-testid="lumi-credit-usage-tab">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-xl font-bold">Credit Usage</h2>
          <p className="text-xs text-neutral-400 mt-1">Every individual message record, per organisation — no manual API calls.</p>
        </div>
        <div className="text-xs text-neutral-400">
          {totals.orgs} orgs · {totals.credits_in_pool.toLocaleString()} credits in pool
        </div>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search organisation, address, manager email, code…"
          data-testid="lumi-credit-usage-search"
          className="px-3 py-2 rounded-lg border border-white/15 bg-white/5 text-sm min-w-[260px] flex-1 placeholder:text-white/30"
        />
      </div>

      {error && <div className="bg-rose-500/10 border border-rose-400/30 text-rose-100 rounded-lg p-3 text-sm">{error}</div>}
      {loading && <div className="text-sm text-white/50">Loading…</div>}

      {!loading && hotels.length === 0 && (
        <div className="text-sm text-white/40 border border-dashed border-white/15 rounded-xl p-8 text-center">
          {q ? "No organisations match those filters." : "No organisations yet."}
        </div>
      )}

      <div className="space-y-2">
        {hotels.map((h) => (
          <div key={h.hotel_id} className="border border-white/10 rounded-xl bg-white/5">
            <button
              onClick={() => setOpenId(openId === h.hotel_id ? null : h.hotel_id)}
              data-testid={`lumi-credit-usage-row-${h.hotel_id}`}
              className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-white/5"
            >
              <div className="min-w-0 pr-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-semibold text-white truncate">{h.hotel_name}</p>
                  <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-white/5 border border-white/15 text-white/60">{h.sector || "hotel"}</span>
                </div>
                <p className="text-xs text-white/60 truncate">{h.manager_email}</p>
                <p className="text-[11px] text-white/40 truncate mt-1">
                  {(h.monthly_credits_remaining ?? 0).toLocaleString()} monthly + {(h.addon_credits_remaining ?? 0).toLocaleString()} add-on = {((h.monthly_credits_remaining ?? 0) + (h.addon_credits_remaining ?? 0)).toLocaleString()} credits available
                </p>
              </div>
              <div className="text-xs text-white/40 whitespace-nowrap">
                {openId === h.hotel_id ? "▼" : "▶"}
              </div>
            </button>

            {openId === h.hotel_id && <UsageDetail hotelId={h.hotel_id} />}
          </div>
        ))}
      </div>
    </div>
  );
}

function UsageDetail({ hotelId }) {
  const [days, setDays] = useState(30);
  const [kind, setKind] = useState("");
  const [codeId, setCodeId] = useState("");
  const [deviceId, setDeviceId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const qs = new URLSearchParams({ days: String(days), limit: "500" });
      if (kind) qs.set("kind", kind);
      if (codeId) qs.set("code_id", codeId);
      if (deviceId) qs.set("device_id", deviceId);
      const r = await lumiFetch(`/hotels/${hotelId}/credit-usage?${qs.toString()}`);
      setData(r);
    } catch (e) {
      setError(e?.data?.detail || "Could not load credit usage");
    } finally {
      setLoading(false);
    }
  }, [hotelId, days, kind, codeId, deviceId]);

  useEffect(() => { load(); }, [load]);

  const t = data?.totals || {};

  return (
    <div className="border-t border-white/10 bg-black/30 px-4 py-4 space-y-3" data-testid={`lumi-credit-usage-detail-${hotelId}`}>
      {loading && <p className="text-xs text-white/50">Loading ledger…</p>}
      {error && <p className="text-xs text-rose-200">{error}</p>}
      {data && (
        <>
          {/* Summary chips */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
            <Chip label="Current balance" value={(t.current_balance || 0).toLocaleString()} highlight />
            <Chip label="Monthly remaining" value={(t.monthly_remaining || 0).toLocaleString()} />
            <Chip label="Add-on remaining" value={(t.addon_remaining || 0).toLocaleString()} />
            <Chip label="Credits used (all time)" value={(t.total_credits_used || 0).toLocaleString()} />
            <Chip label="Membership credits in" value={(t.membership_credits_received || 0).toLocaleString()} />
            <Chip label="Refunds" value={(t.refunds_credited || 0).toLocaleString()} />
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-2 items-center bg-white/5 rounded-lg p-2 border border-white/10">
            <label className="text-[11px] text-white/50">Range</label>
            <select value={days} onChange={(e) => setDays(parseInt(e.target.value, 10))} data-testid="lumi-credit-usage-days" className="text-xs bg-black/40 border border-white/10 rounded px-2 py-1">
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={365}>Last 365 days</option>
            </select>
            <label className="text-[11px] text-white/50 ml-2">Type</label>
            <select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="lumi-credit-usage-kind" className="text-xs bg-black/40 border border-white/10 rounded px-2 py-1">
              <option value="">All types</option>
              {Object.entries(KIND_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <input
              value={codeId}
              onChange={(e) => setCodeId(e.target.value)}
              placeholder="Filter by code_id"
              data-testid="lumi-credit-usage-code-filter"
              className="text-xs bg-black/40 border border-white/10 rounded px-2 py-1 flex-1 min-w-[140px] placeholder:text-white/30"
            />
            <input
              value={deviceId}
              onChange={(e) => setDeviceId(e.target.value)}
              placeholder="Filter by device_id"
              data-testid="lumi-credit-usage-device-filter"
              className="text-xs bg-black/40 border border-white/10 rounded px-2 py-1 flex-1 min-w-[140px] placeholder:text-white/30"
            />
          </div>

          {/* Per-venue and per-device breakdowns */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <BreakdownList title="By venue (credits used)" rows={data.by_venue} testid="lumi-credit-usage-by-venue" />
            <BreakdownList title="By device (credits used)" rows={data.by_device} testid="lumi-credit-usage-by-device" />
          </div>

          {/* Individual message records */}
          <div>
            <div className="flex items-baseline justify-between mb-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-white/50">Individual records</p>
              <p className="text-[11px] text-white/40">Showing {data.records_shown} entries</p>
            </div>
            <div className="border border-white/10 rounded-lg overflow-hidden">
              <table className="w-full text-[11px]" data-testid="lumi-credit-usage-table">
                <thead className="bg-white/5 text-white/60">
                  <tr>
                    <th className="text-left px-2 py-1.5">When</th>
                    <th className="text-left px-2 py-1.5">Type</th>
                    <th className="text-left px-2 py-1.5">Venue</th>
                    <th className="text-left px-2 py-1.5">Device</th>
                    <th className="text-right px-2 py-1.5">Amount</th>
                    <th className="text-right px-2 py-1.5">Balance after</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {data.records.length === 0 && (
                    <tr><td colSpan={6} className="px-2 py-4 text-center text-white/40">No ledger entries in this window.</td></tr>
                  )}
                  {data.records.map((r, i) => (
                    <tr key={r.transaction_id || i} data-testid={`lumi-credit-usage-record-${i}`} className="hover:bg-white/5">
                      <td className="px-2 py-1.5 text-white/70 whitespace-nowrap">{r.at ? fmtDateTime(r.at) : "—"}</td>
                      <td className="px-2 py-1.5 text-white/70">{KIND_LABEL[r.kind] || r.kind || "—"}</td>
                      <td className="px-2 py-1.5 text-white/70 truncate max-w-[160px]" title={`${r.venue_label || ""} · ${r.code || ""}`}>{r.venue_label || r.code || "—"}</td>
                      <td className="px-2 py-1.5 text-white/60 truncate max-w-[160px]" title={r.device_id || ""}>{r.device_hint || r.device_id || "—"}</td>
                      <td className={`px-2 py-1.5 text-right font-mono ${r.direction === "debit" ? "text-rose-200" : "text-emerald-200"}`}>
                        {r.direction === "debit" ? "−" : "+"}{Math.abs(r.amount || 0).toLocaleString()}
                      </td>
                      <td className="px-2 py-1.5 text-right text-white/50 font-mono">{r.balance_after != null ? Number(r.balance_after).toLocaleString() : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Chip({ label, value, highlight }) {
  return (
    <div className={`rounded-lg p-2 ${highlight ? "bg-amber-500/10 border border-amber-300/30" : "bg-white/5 border border-white/10"}`}>
      <p className="text-[10px] uppercase tracking-wide text-white/50">{label}</p>
      <p className={`text-sm font-semibold ${highlight ? "text-amber-100" : "text-white"}`}>{value}</p>
    </div>
  );
}

function BreakdownList({ title, rows, testid }) {
  return (
    <div className="bg-white/5 border border-white/10 rounded-lg p-2" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-wide text-white/50 mb-1">{title}</p>
      {(!rows || rows.length === 0) && <p className="text-[11px] text-white/40 py-1">No usage in this window.</p>}
      <ul className="space-y-0.5 max-h-40 overflow-y-auto">
        {(rows || []).map((r, i) => (
          <li key={i} className="flex items-center justify-between text-[11px] text-white/70">
            <span className="truncate mr-2">{r.label}</span>
            <span className="font-mono text-white">{(r.credits_used || 0).toLocaleString()}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
